/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    Renderer2
} from '@angular/core';
import { DragDropService, LogService, UtilsService } from '../../services';

@Directive({
    selector: '[appDragNDrop]'
})

export class DragNDropDirective implements AfterViewInit {
    /** Stops an instance of the directive from being droppable */
    @Input() public dropEnabled: boolean = true;

    /** If set the drop area will be highlighted */
    @Input() public highlightDropArea: boolean = false;

    /** The message displayed on an empty drop zone */
    @Input() public noItemsMessage: string = 'No items';

    /** Sets the logic for adding borders to the correct dimension based on whether the drop zones are aligned
     * left and right or up and down in the view */
    @Input() public zoneAlignment: 'top-bottom' | 'left-right' = 'left-right';

    /** Emits when drag starts */
    @Output() public nDragStart: EventEmitter<any> = new EventEmitter();

    /** Emits when drag ends */
    @Output() public nDragEnd: EventEmitter<any> = new EventEmitter();

    /** Emits when drag enters a drop zone */
    @Output() public nDragEnter: EventEmitter<any> = new EventEmitter();

    /** Emits when drag leave a drop zone */
    @Output() public nDragLeave: EventEmitter<any> = new EventEmitter();

    /** Emits when drag is over a drop zone */
    @Output() public nDragOver: EventEmitter<any> = new EventEmitter();

    /** Emits when a drag item is dropped onto a drop zone */
    @Output() public nDrop: EventEmitter<any> = new EventEmitter();

    /** Emits all the ordered selected data after item has been dropped */
    @Output() public selectedData: EventEmitter<any> = new EventEmitter();

    /** Store the item being drags Id*/
    private dragItemId: string = '';

    /** Stores if you are over a drop zone */
    private isOverDropZone: boolean = false;

    constructor(
        private dragService: DragDropService,
        private hostElement: ElementRef,
        private logService: LogService,
        private renderer: Renderer2,
        private utilsService: UtilsService
    ) {
        // Add class to host for reference in the logic
        renderer.addClass(hostElement.nativeElement, 'target-zone');
    }

    /**
     * Run as the drag starts
     * @param event
     */
    @HostListener('dragstart', ['$event'])
    public dragStart(event: any): void {
        event.stopPropagation();
        if (!event.target.id) {
            this.logService.error(
                'DragNDropDirective: dragStart',
                'There is no id attribute set for the drag items'
            );
        } else {
            // Set the data of the item being dragged
            event.dataTransfer.setData('text/plain', event.target.id);
            // Store item id for use in dragend
            this.dragItemId = event.target.id;

            this.styleDragElement(event.target, true);

            // Delay the hide class, so it still shows while dragging but disappears from start position.
            const timerId = setTimeout(() => {
                event.target.classList.add('hide-item');
                clearTimeout(timerId);
            }, 0);
            // Emit the event
            this.nDragStart.next(event);
            this.dragService.dragActive.next(true);
        }
    }

    /**
     * Capture item being dropped outside either target zone and remove their styling.
     * @param event
     */
    @HostListener('dragend', ['$event'])
    public dragEnd(event: any): void {
        event.preventDefault();
        if (this.dragItemId) {
            const dragElement = document.getElementById(this.dragItemId);
            dragElement.classList.remove('hide-item');
            this.removeNoItemsMessage();
        }

        if (this.highlightDropArea) {
            this.removeHighlight();
        }

        // Emit the event
        this.nDragEnd.next(event);
        this.dragService.dragActive.next(false);
        this.styleDragElement(event.target, false);
    }

    /**
     * Run as mouse enters a target zone
     * @param event
     */
    @HostListener('dragenter', ['$event'])
    public dragEnter(event: any): void {
        event.preventDefault();
        event.stopPropagation();

        this.removeNoItemsMessage();

        if (this.highlightDropArea && this.eventIncludesClass(event, 'target-zone')) {
            event.target.classList.add('drag-over');
        }

        // Emit the event
        this.nDragEnter.next(event);
    }

    /**
     * Remove any borders that have been set when the dragging item leaves a target zone
     * @param event
     */
    @HostListener('dragleave', ['$event'])
    public dragLeave(event: any): void {
        if (this.highlightDropArea) {
            this.removeHighlight();
        }

        if (this.eventIncludesClass(event, 'drag-item')) {
            this.removeStyling(event);
        }

        if (this.highlightDropArea && this.eventIncludesClass(event, 'target-zone')) {
            event.target.classList.remove('drag-over');
        }

        // Check to see if the host has content.
        const hostItems = this.hostElementHasDragItems();
        if (!this.isOverDropZone && (hostItems === 1 || hostItems === 0)) {
            this.createNoItemsMessage();
        } else {
            this.removeNoItemsMessage();
        }

        this.isOverDropZone = false;
        // Emit the event
        this.nDragLeave.next(event);
    }

    /**
     * Fires all the time that dragging is happening.
     * @param event
     */
    @HostListener('dragover', ['$event'])
    public dragOver(event: any): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.eventIncludesClass(event, 'drag-item')) {
            this.setStyling(event);
        }

        this.isOverDropZone = true;

        // Emit the event
        this.nDragOver.next(event);
    }

    /**
     * Fires when an item is dropped on a target zone.
     * @param event
     */
    @HostListener('drop', ['$event'])
    public drop(event: any): void {
        event.preventDefault();

        // Before changing the DOM position and styling of a drag item, make sure it is over a drop zone.
        if (this.isOverDropZone && event.dataTransfer) {
            const id = event.dataTransfer.getData('text/plain');
            const dragElement = document.getElementById(id);

            if (dragElement?.classList) {
                dragElement.classList.remove('hide-item');
                this.attachDragItemToContainer(event, dragElement);
            }

            if (this.eventIncludesClass(event, 'drag-item')) {
                this.removeStyling(event);
            }

            if (this.highlightDropArea) {
                event.target.classList.remove('drag-over');
            }

            this.dragService.dropOccurred.next(true);

            // Emit the event
            this.nDrop.next(event);
            this.setLastElementStyle();
        }
    }

    /**
     * Set up the page
     */
    public ngAfterViewInit(): void {
        this.contentInitialSetup();
        this.setupSubscribers();
        this.setZoneIds();

        if (!this.hostElementHasDragItems()) {
            this.createNoItemsMessage();
        }
    }

    /**
     * Style dragged element.
     * @param target
     * @param toggle
     * @private
     */
    private styleDragElement(target: HTMLElement, toggle: boolean): void {
        const style: string = toggle ? '1px solid #b2b2b2' : 'none';
        const background: string = toggle ? 'white' : 'none';
        target.style['border-top'] = style;
        target.style['border-left'] = style;
        target.style['border-right'] = style;
        target.style['background'] = background;
    }

    /**
     * Set the styling for the last drag item.
     * @private
     */
    private setLastElementStyle(): void {
        const items = Array.from(document.getElementsByClassName('drag-item'));
        items[items.length - 1].setAttribute('style', 'border-bottom: 1px solid #b2b2b2');
    }

    /**
     * Adds the dragged item to the correct container depending on where it is dropped.
     * @param event
     * @param dragElement
     * @private
     */
    private attachDragItemToContainer(event: any, dragElement: HTMLElement): void {
        if (this.eventIncludesClass(event, 'drag-item')) {
            const target = event.target;
            const targetStyle = target.style;

            if (targetStyle['border-bottom'] !== 'none') {
                target.parentNode.insertBefore(dragElement, event.target.nextSibling);
            } else {
                target.parentNode.insertBefore(dragElement, event.target);
            }
        }

        if (this.eventIncludesClass(event, 'target-zone')) {
            event.target.appendChild(dragElement);
        }
    }

    /**
     * Set styling based on zone alignment, calculates the size of the drag item and add the appropriate border
     * to give appearance that the item has moved to make space for the new one.
     * @param event
     * @private
     */
    private setStyling(event: any): void {
        const target = event.target;
        const bounding = target.getBoundingClientRect();
        const targetStyle = target.style;

        if (this.zoneAlignment === 'left-right') {
            const offset = bounding.y + (bounding.height / 2);
            if (event.clientY - offset > 0) {
                targetStyle['border-bottom'] = `solid ${bounding.height / 2}px transparent`;
                targetStyle['border-top'] = 'none';
            } else {
                targetStyle['border-top'] = `solid ${bounding.height / 2}px transparent`;
                targetStyle['border-bottom'] = 'none';
            }
        } else {
            const offset = bounding.x + (bounding.width / 2);
            if (event.clientY - offset > 0) {
                targetStyle['border-left'] = `solid ${bounding.width / 2}px transparent`;
                targetStyle['border-right'] = 'none';
            } else {
                targetStyle['border-right'] = `solid ${bounding.width / 2}px transparent`;
                targetStyle['border-left'] = 'none';
            }
        }
    }

    /**
     * Style the drag item based on alignment.
     * @param event
     * @private
     */
    private removeStyling(event: any): void {
        const targetStyle = event.target.style;
        if (this.zoneAlignment === 'left-right') {
            targetStyle['border-bottom'] = '1px solid #b2b2b2';
            targetStyle['border-top'] = 'none';
        } else {
            targetStyle['border-left'] = 'none';
            targetStyle['border-right'] = 'none';
        }
    }

    /**
     * Setup subscribers
     * @private
     */
    private setupSubscribers(): void {
        this.dragService.dropOccurred.subscribe({
            next: (): void => {
                const dataOut = this.getDropZoneData();
                if (!dataOut.length) {
                    this.createNoItemsMessage();
                }
                this.selectedData.emit(dataOut);
            }
        });

        this.dragService.resetNodePosition.subscribe({
            next: (node: any): void => {
                this.checkNodeInHostElement(node);
            }
        });

        this.dragService.reRunInitialSetup.subscribe({
            next: (): void => {
                this.contentInitialSetup();
            }
        });
    }

    /**
     * Used on items that contain a remove button to make sure they go to a different target zone.
     * @param nodeToMove
     * @private
     */
    private checkNodeInHostElement(nodeToMove: any): void {
        const hostChildNodes = this.utilsService.getObjectValue(
            this.hostElement,
            'nativeElement.childNodes'
        );

        // Check if the host has the node to move
        const hasNode = Array.from(hostChildNodes).find(node => node === nodeToMove);

        // If it doesn't append it to the host.
        const timerId = setTimeout(() => {
            if (!hasNode) {
                this.removeNoItemsMessage();
                this.hostElement.nativeElement.appendChild(nodeToMove);
                this.dragService.dropOccurred.next(true);
            }
            clearTimeout(timerId);
        }, 50);
    }

    /**
     * Sets up the drag items with the correct attributes for other logic in this directive.
     * @private
     */
    private contentInitialSetup(): void {
        const hostChildNodes = this.utilsService.getObjectValue(
            this.hostElement,
            'nativeElement.childNodes'
        );

        if (hostChildNodes) {
            const childData = [];
            let index = 0;

            for (const node of hostChildNodes) {
                if (node.classList?.contains('drag-item')) {
                    // Allow for multiple drag 'n' drops on one page by adding random number to id.
                    const randomNumber = Math.floor(1000 + Math.random() * 9000);
                    node.id = `drag-item-${randomNumber}-${index}`;
                    node.draggable = true;

                    this.checkForChildImages(node);
                    this.checkForRemoveButton(node);

                    if (node.dataset?.form) {
                        childData.push(node.dataset.form);
                    }
                    // Only increment on drag-items
                    index++;
                }
            }

            if (index !== childData.length) {
                this.logService.error(
                    'DragNDropDirective: contentInitialSetup',
                    'One or more drag items are missing [attr.data-form], this is used to pass back data to the parent component.'
                );
            }
        } else {
            this.logService.warn(
                'DragNDropDirective: contentInitialSetup',
                'There are no drag items in this drag and drop zone'
            );
        }
    }

    /**
     * Check for child images and disable drag on them
     * @param node
     * @private
     */
    private checkForChildImages(node: any): void {
        const images = node.querySelectorAll('img');
        for (const image of images) {
            image.draggable = false;
        }
    }

    /**
     * If the drag item has a remove button add a click handler
     * @param node
     * @private
     */
    private checkForRemoveButton(node: any): void {
        const removeButton = node.querySelector('.remove-item');
        if (removeButton) {
            removeButton.onclick = () => this.dragService.resetNodePosition.next(node);
        }

    }

    /**
     * Check to see if the host has drag items
     * @private
     */
    private hostElementHasDragItems(): number {
        const hostChildNodes = this.utilsService.getObjectValue(
            this.hostElement,
            'nativeElement.childNodes'
        );

        if (hostChildNodes) {
            const dragItems = [];

            for (const node of hostChildNodes) {
                if (node.classList?.contains('drag-item')) {
                    dragItems.push(node);
                }
            }

            return dragItems.length;
        }
    }

    /**
     * Create a no items message.
     * @private
     */
    private createNoItemsMessage(): void {
        const noItemsMessage = this.hostElement.nativeElement.querySelector('#no-items');

        if (!noItemsMessage) {
            // Styling here to keep styling consistence across application.
            const addedStyle = {
                'align-items': 'center',
                'display': 'flex',
                'height': '100%',
                'justify-content': 'center',
                'width': '100%'
            };

            const messageElement = document.createElement('div');
            messageElement.id = 'no-items';

            // Assign each style property to the new message element.
            Object.keys(addedStyle).forEach(element => {
                messageElement.style.setProperty(`${element}`, addedStyle[element]);
            });
            messageElement.innerHTML = `<span>${this.noItemsMessage}</span>`;
            this.hostElement.nativeElement.appendChild(messageElement);
        }
    }

    /**
     * Remove no items message if it exists.
     * @private
     */
    private removeNoItemsMessage(): void {
        const noItemsMessage = this.hostElement.nativeElement.querySelector('#no-items');
        if (noItemsMessage) {
            noItemsMessage.remove();
        }
    }

    /**
     * Gets dropzone data from the host element
     * @private
     */
    private getDropZoneData(): any {
        const childNodes = this.utilsService.getObjectValue(
            this.hostElement,
            'nativeElement.childNodes'
        );

        if (childNodes) {
            const childData = [];
            for (const node of childNodes) {
                if (node.dataset?.form) {
                    childData.push(node.dataset.form);
                }
            }

            return childData;
        }
    }

    /**
     * Checks event includes a specific class name.
     * @param event
     * @param className
     * @private
     */
    private eventIncludesClass(event: any, className: string): boolean {
        const classList = this.utilsService.getObjectValue(
            event,
            'target.classList'
        );

        return classList?.contains(className);
    }

    /**
     * Remove target zone highlight.
     * @private
     */
    private removeHighlight(): void {
        const targetZoneCollection = document.querySelectorAll('.target-zone');
        const targetZoneElements = Array.from(targetZoneCollection);
        for (const node of targetZoneElements) {
            node.classList.remove('drag-over');
        }
    }

    /**
     * Assign ids to the drag and drop zones for GI testing.
     * @private
     */
    private setZoneIds(): void {
        const zonesCollection = document.querySelectorAll('.target-zone');
        const zonesElements = Array.from(zonesCollection);
        for (const zone of zonesElements) {
            const zoneIndex = zonesElements.indexOf(zone);
            zone.setAttribute('id', `zone${zoneIndex}`);
        }
    }
}
